<template>
  <div>
    <v-dialog
      v-if="type === 'table'"
      :value.sync="settingDialog"
      persistent
      :fullscreen="iOS && inIframe"
      scrollable
      max-width="320"
    >
      <v-card v-if="!noWebCam">
        <v-card-title>{{ currentTableTitle }}</v-card-title>
        <v-divider></v-divider>
        <v-card-text>
          <v-layout
            row
            align-center
            mb-2
          >
            <v-flex
              xs12
              class="text-xs-left"
            >
              {{ $t('scan_qr_camera') }}
            </v-flex>
          </v-layout>
          <v-layout
            row
            justify-center
          >
            <video
              id="video"
              width="290"
              height="290"
              style="border: 1px solid gray;"
            ></video>
          </v-layout>
        </v-card-text>
      </v-card>
      <v-card v-else>
        <v-card-title>{{ $t('no_camera') }}</v-card-title>
        <v-divider></v-divider>
        <v-card-text style="height: 160px;">
          <v-layout
            row
            align-center
            mb-2
          >
            <v-flex
              xs12
              class="text-xs-left"
            >
              {{ $t('no_camera_info') }}
            </v-flex>
          </v-layout>
        </v-card-text>
        <v-card-actions>
          <v-btn
            :color="colors.button1a"
            flat
            block
            @click.native="closeDialog"
          >Close</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
    <v-dialog
      v-else-if="type === 'address' && this.modeSelected"
      :value.sync="settingDialog"
      persistent
      :fullscreen="iOS && inIframe"
      scrollable
      max-width="320"
    >
      <v-card>
        <v-card-title :style="timeTitleStyle">{{ hasUserAddress && !insertPostal ? $t('select_from_address') : $t('check_deliverable') }}</v-card-title>
        <v-divider></v-divider>
        <v-card-text class="pa-0">
          <div
            v-if="hasUserAddress && !insertPostal"
            class="mb-2"
          >
            <div
              v-for="userAddress in addresses"
              :key="userAddress.id"
            >
              <address-card :user-address="userAddress" @select-address="selectAddress(userAddress)" />
              <v-divider></v-divider>
            </div>
          </div>
          <v-layout
            v-else
            row
            align-center
            mb-2
          >
            <v-flex
              xs12
              class="text-xs-left pl-2 pt-2 pr-2"
            >
              <v-text-field
                v-model="address.postal_code"
                ref="postalCodeField"
                name="postal_code"
                class="postal-code"
                :label="$t('postal_code')"
                @keyup="fetchDeliveryInfo"
                single-line
                hide-details
              ></v-text-field>
            </v-flex>
          </v-layout>
          <v-layout
            row
            align-center
            mb-2
          >
            <v-flex
              v-if="address.postal_code && address.postal_code.length >= 6"
              xs12
              class="pl-2 pt-1 pr-2 text-xs-left"
            >
              <v-btn
                icon
                small
                class="ma-0"
              >
                <v-icon
                  small
                  :color="addressIcon.color"
                >{{ addressIcon.icon }}</v-icon>
              </v-btn>
              <span :style="addressResultStyle">{{ addressMsg }}</span>
            </v-flex>
          </v-layout>
        </v-card-text>
        <v-card-actions class="pt-0">
          <v-btn
            v-if="hasUserAddress && insertPostal"
            :color="colors.button1a"
            flat
            block
            @click.native="insertPostal = false"
          >{{ $t('my_address') }}</v-btn>
          <v-btn
            v-if="hasUserAddress && !insertPostal"
            :color="colors.button1a"
            flat
            block
            @click.native="insertPostal = true"
          >{{ $t('use_postal_code') }}</v-btn>
          <v-btn
            :color="colors.button1a"
            flat
            block
            @click.native="savePostalCode"
          >Ok</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
    <v-dialog
      v-else-if="type === 'timer' && this.modeSelected"
      :value.sync="settingDialog"
      persistent
      :fullscreen="iOS && inIframe"
      scrollable
      max-width="380"
    >
      <v-card>
        <v-card-title :style="timeTitleStyle">{{ selectTimeTitle }}</v-card-title>
        <v-divider />
        <horizontal-scroll
          v-if="dates.length > 1"
          class="ma-2"
        >
          <div
            v-for="(date, index) in dates"
            :key="index"
            class="mr-2 text-xs-center"
            :style="dayStyle(date.date, date.hasSlots)"
            @click="selectDate(date.date, date.hasSlots)"
          >
            <v-layout column>
              <v-flex>
                <span class="smaller">{{ date.month.toUpperCase() }}</span>
              </v-flex>
              <v-flex>
                {{ date.day }}
              </v-flex>
              <v-flex>
                <span class="smaller">{{ date.weekDay.toUpperCase() }}</span>
              </v-flex>
            </v-layout>
          </div>
        </horizontal-scroll>
        <v-card-text :style="timerStyle">
          <v-container
            v-if="loadingTime"
            fill-height
          >
            <v-layout row>
              <v-flex
                xs12
                class="text-xs-center"
              >
                <v-icon small>fas fa-circle-notch fa-spin</v-icon>
              </v-flex>
            </v-layout>
          </v-container>
          <v-radio-group
            v-model="selectedTime"
            v-else-if="times && times.length > 0"
            column
            class="mt-0"
          >
            <v-radio
              v-for="t in times"
              :key="t.time"
              :label="t.availTime <= 0 ? t.time + ' (Unavailable)' : t.label"
              :value="t.time"
              :disabled="t.availTime <= 0"
            />
          </v-radio-group>
          <v-container
            v-else
            fill-height
          >
            <v-layout row>
              <v-flex
                xs12
                class="text-xs-center"
              >
                <span class="font-italic">{{ $t('no_available_slot') }}</span>
              </v-flex>
            </v-layout>
          </v-container>
        </v-card-text>
        <v-divider></v-divider>
        <v-card-actions>
          <!-- <v-btn
            :color="colors.button1a"
            flat
            @click.native="dialog = false"
          >{{ $t('btn_close') }}</v-btn> -->
          <v-btn
            :color="colors.button1a"
            flat
            @click="saveTime"
            block
            :disabled="loadingTime"
          >{{ $t('ok') }}</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
    <v-dialog
      v-else-if="type === 'takeaway_contact' && this.modeSelected"
      :value.sync="settingDialog"
      persistent
      :fullscreen="iOS && inIframe"
      scrollable
      max-width="320"
    >
      <v-card>
        <v-card-title :style="timeTitleStyle">{{ $t('please_enter_a_takeaway_contact') }}</v-card-title>
        <v-divider></v-divider>
        <v-card-text class="pa-0">
          <v-text-field
            v-model="takeaway_contact.name"
            name="takeaway_contact_name"
            class="takeaway-contact"
            :label="$t('contact')"
            hide-details
          ></v-text-field>
          <v-text-field
            v-model="takeaway_contact.phone"
            name="takeaway_contact_phone"
            class="takeaway-contact"
            :label="$t('contact_phone')"
            hide-details
          ></v-text-field>
        </v-card-text>
        <v-card-actions class="pt-0">
          <v-btn
            :color="colors.button1a"
            :disabled="isTakeawaySaveButtonDisabled"
            flat
            block
            @click.native="saveTakeawayContact"
          >{{ $t('submit') }}</v-btn>
          <v-btn
            :color="colors.button1a"
            flat
            block
            @click.native="cancelTakeawayContact"
          >{{ $t('cancel') }}</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import { mapGetters } from 'vuex'
import { BrowserQRCodeReader } from '@zxing/library'
import AddressCard from '~/components/AddressCard'
import HorizontalScroll from 'vue-horizontal-scroll'

export default {
  name: 'setting-dialog',
  components: {
    'address-card': AddressCard,
    'horizontal-scroll': HorizontalScroll
  },
  data: () => ({
    selectedTable: null,
    noWebCam: false,
    codeReader: null,
    insertPostal: false,
    loadingTime: false,
    selectedDate: null,
    selectedTime: null,
    takeaway_contact: {
      name: null,
      phone: null
    },
  }),
  computed: {
    ...mapGetters('auth', [
      'user', 'addresses'
    ]),
    ...mapGetters('config', [
      'theme', 'modeSelected', 'modes', 'iOS', 'inIframe', 'config', 'colors',
      'tbdata', 'tables', 'mode', 'rid'
    ]),
    ...mapGetters('dialog', [
      'settingDialog', 'type'
    ]),
    ...mapGetters('delivery', [
      'address', 'deliveryInfo', 'isDeliverable'
    ]),
    ...mapGetters('cart', [
      'pickupDate', 'pickupTime', 'cartQty', 'dates', 'times'
    ]),
    ...mapGetters('area', [
      'selectedAreaMode'
    ]),
    tableList () {
      if (!this.tables)
        return []
      return this.tables.filter(t => parseInt(t.IsTakeaway) === 0 && t.Name.toUpperCase() !== 'NOTABLE')
    },
    currentTableTitle () {
      if (!this.tbdata) {
        return this.$t('set_table')
      }
      return this.$t('change_from_table') + this.tbdata.Name
    },
    startStyle () {
      return {
        fontWeight: 600,
        color: this.colors.button1a
      }
    },
    addressResultStyle () {
      let styles = {
        fontSize: '14px',
      }
      if (this.isDeliverable) {
        styles.color = this.theme.orders_success_color
      } else {
        styles.color = this.theme.orders_fail_color
      }
      return styles
    },
    addressIcon () {
      return {
        icon: this.isDeliverable ? 'fas fa-check' : 'fas fa-times',
        color: this.isDeliverable ? this.theme.orders_success_color : this.theme.orders_fail_color
      }
    },
    addressMsg () {
      return !this.isDeliverable ? (this.deliveryInfo.msg ? this.deliveryInfo.msg : this.$t('postal_code_not_ready'))
        : (this.deliveryInfo.msg ? this.deliveryInfo.msg : '')
    },
    hasUserAddress () {
      return this.user && this.addresses && this.addresses.length > 0
    },
    timerStyle () {
      return {
        height: this.times && this.times.length > 0 ? '280px' : '100px'
      }
    },
    timerTextStyle () {
      return {
        color: this.theme.address_text_color
      }
    },
    timeTitle () {
      if (this.mode.isTakeaway) return this.$t('pickup_time')
      if (this.mode.isDelivery) return this.$t('delivery_time')
    },
    selectTimeTitle () {
      if (this.mode.isTakeaway) return this.$t('select_pickup_time')
      if (this.mode.isDelivery) return this.$t('select_delivery_time')
    },
    timeTitleStyle () {
      return {
        fontWeight: 800,
        color: this.theme.toolbar_color,
        background: this.theme.toolbar_bg_color
      }
    },
    isTakeawaySaveButtonDisabled() {
      return !(this.takeaway_contact.name && this.takeaway_contact.phone && this.takeaway_contact.phone.length >= 8);
    }
  },
  methods: {
    closeDialog () {
      this.codeReader.reset()
      this.$store.dispatch('dialog/hideSettingDialog')
    },
    saveTable () {
      const table = this.tables.find(t => t.Name === this.selectedTable)
      if (table) {
        this.$store.dispatch('changeTable', table.qr_code)
      }
      this.$store.dispatch('dialog/hideSettingDialog')
    },
    async fetchDeliveryInfo () {
      if (this.address.postal_code) {
        if (this.address.postal_code.length === 6) {
          await this.$store.dispatch('delivery/fetchDeliveryInfo', this.address.postal_code)
        } else {
          this.$store.dispatch('delivery/resetDeliveryInfo')
        }
      }
    },
    async savePostalCode () {
      // Do not reset address if use press Ok from address list
      if (!this.hasUserAddress || this.insertPostal) {
        this.$store.dispatch('delivery/setAddress')
        this.prepareTimer()
        this.$store.dispatch('dialog/showSettingDialog', 'timer')
      }

      // user has address but click on 'ok' btn instead on selecting address
      if (this.hasUserAddress && !this.insertPostal) {
        let defaultAddress = this.addresses.find(a => a.default)
        await this.$store.dispatch('delivery/fetchDeliveryInfo', defaultAddress.postal_code)
        this.selectAddress(defaultAddress)
      }

      // this.prepareTimer()
      // this.$store.dispatch('dialog/showSettingDialog', 'timer')
    },
    async saveTakeawayContact () {
      console.log(this.takeaway_contact)
      await this.$store.dispatch('takeaway/setContact', this.takeaway_contact)
      this.$store.dispatch('dialog/hideSettingDialog')
      this.$bus.$emit('TakeawayContactInputCompleted', 1);
    },
    async cancelTakeawayContact () {
      console.log(this.takeaway_contact)
      await this.$store.dispatch('takeaway/resetContact')
      this.$store.dispatch('dialog/hideSettingDialog')
    },
    selectAddress (address) {
      this.$store.dispatch('delivery/setAddress', address)
      if (this.isDeliverable) {
        // this.$store.dispatch('dialog/hideSettingDialog')
        this.prepareTimer()
        this.$store.dispatch('dialog/showSettingDialog', 'timer')
      }
    },
    async prepareTimer () {
      this.$bus.$emit('showFullLoading', true)
      await this.$store.dispatch('cart/fetchTakeawayDates')
      await this.$store.dispatch('cart/checkPickupDate')
      if (!this.pickupDate) {
        let dateWithSlots = this.dates.find(d => d.hasSlots)
        if (dateWithSlots) await this.selectDate(this.dates.find(d => d.hasSlots).date)
      } else {
        await this.selectDate(this.pickupDate)
      }
      this.$bus.$emit('showFullLoading', false)
    },
    dayStyle (date, hasSlots = true) {
      let styles = {
        border: '1px solid #8a8a8a',
        padding: '4px',
        display: 'inline-block',
        cursor: 'pointer'
      }
      if (this.selectedDate === date && hasSlots) {
        styles.fontWeight = 800
        styles.background = this.theme.toolbar_bg_color
        styles.color = this.theme.toolbar_color
        styles.borderColor = this.theme.toolbar_color
      }
      if (!hasSlots) {
        styles.color = 'grey'
        styles.borderColor = 'grey'
      }
      styles.width = this.$mq === 'xs' ? '65px' : '75px'
      return styles
    },
    saveTime () {
      if (this.cartQty > 0 && (this.pickupDate !== this.selectedDate || this.pickupTime !== this.selectedTime)) {
        const text = 'If you change time, your selections in basket will be cleared. Are you sure?'
        this.$store.dispatch('responseMessage', {
          type: 'error',
          title: 'Start New Order',
          text: text,
          modal: true,
          buttons: [
            { text: 'Cancel' },
            { text: 'Ok', action: 'changeTime'}
          ]
        })
      } else {
        this.$store.dispatch('cart/setPickupDate', this.selectedDate)
        this.$store.dispatch('cart/setPickupTime', this.selectedTime)
      }
      this.$store.dispatch('dialog/hideSettingDialog')
    },
    async selectDate (date, hasSlots = true) {
      // if (date === this.selectedDate) return
      if (!hasSlots) {
        return
      }
      this.loadingTime = true
      this.selectedDate = date
      await this.$store.dispatch('cart/fetchTakeawayTimes', date)
      this.selectedTime = this.pickupTime ? this.pickupTime : this.times && this.times.length > 0 ? this.times[0].time : null
      this.loadingTime = false
    }
  },
  watch: {
    async settingDialog (val) {
      if (this.type === 'table') {
        if (parseInt(this.rid) === 977) {
          const text = 'Please scan the qr code with your QR scanner.'
          const vm = this
          vm.$store.dispatch('responseMessage', {
            type: 'success',
            title: 'Scan QR',
            text: text,
            modal: true
          })
          vm.$store.dispatch('dialog/hideSettingDialog')
        } else {
          // Refetch everytime because we have dynamic tables
          await this.$store.dispatch('config/fetchTables')
          this.codeReader = new BrowserQRCodeReader()
          if (val && !this.noWebCam) {
            // Declaring as constant to make it readable in function (global)
            const vm = this
            this.codeReader
              .listVideoInputDevices()
              .then(videoInputDevices => {
                this.codeReader
                .decodeFromInputVideoDevice(undefined, 'video')
                .then(result => {
                  const url = new URL(result.text)
                  const q = url.searchParams.get('q') || url.searchParams.get('t')
                  vm.$store.dispatch('changeTable', q)
                  if (!vm.tbdata) {
                    vm.$store.dispatch('responseMessage', {
                      type: 'error',
                      title: 'Invalid table',
                      text: 'Table is invalid. Please try again.',
                      modal: true
                    })
                  }
                  vm.codeReader.reset()
                  vm.$store.dispatch('dialog/hideSettingDialog')
                })
                .catch(err => {
                  // Camera permission denied / Video stream has ended before any code could be detected
                  if (err.message.includes('permission') || err.message.includes('Permission')) {
                    vm.noWebCam = true
                  }
                })
              })
              .catch(err => {
                // No camera
                vm.noWebCam = true
              })
          }
        }
      }
      if (this.type === 'timer') {
        if (val) {
          await this.prepareTimer()
        }
      }
    }
  },
  async mounted () {
    if (!this.settingDialog) return
    if ((!this.modeSelected && this.modes.length > 1 && !this.selectedAreaMode)) {
      await this.$store.dispatch('dialog/hideSettingDialog')
      return
    }
    if (this.type === 'table') {
      this.selectedTable = this.tbdata ? this.tbdata.Name : ''
    } else if (this.type === 'address') {
      if (this.user) {
        await this.$store.dispatch('auth/fetchAddresses')
      }
      await this.$store.dispatch('delivery/fetchDeliveryInfo', this.address.postal_code)
      if (!this.address.id) {
        this.insertPostal = true
      }
      if (this.modes.length === 1) {
        await this.$store.dispatch('config/setModeSelected')
      }
    }
    if (this.type === 'timer' || this.type === 'address' || this.type === 'takeaway_contact') {
      if (this.modes.length === 1) {
        await this.$store.dispatch('config/setModeSelected')
      }
      await this.prepareTimer()
    }
  }
}
</script>

<style scoped>
.name {
  font-weight: 700;
}
.change {
  font-size: 11px;
}
.qrcode-text-btn {
  display: inline-block;
  cursor: pointer;
  margin-top: 15px;
}
.qrcode-text-btn > input[type=file] {
  position: absolute;
  overflow: hidden;
  width: 0;
  height: 0;
  opacity: 0;
}
#video {
  object-fit: fill;
}
.postal-code {
  padding: 5px 0 0 10px;
  border: 1px solid gray;
  background-color: white;
}
.takeaway-contact {
  padding: 5px 0 0 10px;
  /*border: 1px solid gray;*/
  background-color: white;
  margin: 10px;
}
.smaller {
  font-size: 12px;
}
.grey-color {
  color: #8a8a8a;
}
</style>



// WEBPACK FOOTER //
// src/components/SettingDialog.vue